import clsx from 'clsx';
import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  Stack,
  StandardCheckbox
} from 'components';
import AccordionComponent from 'components/accordion/accordion';
import { useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getInsurancePlans, getInsurancesAutocomplete } from 'store/clients/clientsActions';
import { clearInsurancePlans } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import { FormValues, Insurance as InsuranceType } from '../../newClientForm';
import { isSectionInvalid } from '../personalDetails/personalDetails';
import SectionTitle from '../sectionTitle/sectionTitle';
import style from './insurance.module.scss';
import InsurancePlanSelect from './insurancePlanSelect/insurancePlanSelect';

const secondaryInsuranceInitialValues = {
  insuredSameAsClient: true,
  insuranceCategory: '',
  otherInsuranceCategory: '',
  insurancePlanId: null,
  insurancePlanName: '',
  otherInsurancePlan: '',
  insuranceNumber: '',
  groupNumber: '',
  relationshipToClient: '',
  insuredName: '',
  insuredDateOfBirth: '',
  insuredStreetAddress: '',
  insuredStreetAddress2: '',
  insuredCity: '',
  insuredState: '',
  insuredZipCode: ''
} satisfies InsuranceType;

const Wrapper = ({
  children,
  ...props
}: { children: React.ReactNode } & ComponentProps<typeof Stack>) => {
  const { isTablet } = useResponsive();

  return (
    <Stack
      width="100%"
      display="flex"
      flexDirection={isTablet ? 'column' : 'row'}
      gap={isTablet ? '4px' : '24px'}
      {...props}
    >
      {children}
    </Stack>
  );
};

const InsurancePlanField = ({
  insuranceType
}: {
  insuranceType: 'primaryInsurance' | 'secondaryInsurance';
}) => {
  const formik = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const [searchInputValue, setSearchInputValue] = useState('');
  const pendingGetInsurancePlansRef = useRef<{ abort: () => void } | null>(null);
  const insurancePlans = useAppSelector((state) => state.clients.insurancePlans.data);
  const { organizationId } = getLsUserRoleObject();

  const debouncedSearch = useCallback(
    debounce(
      (
        query: string,
        _pendingGetInsurancePlansRef: React.MutableRefObject<{ abort: () => void } | null>
      ) => {
        if (query) {
          const promise = dispatch(getInsurancePlans({ organizationId, term: query }));
          _pendingGetInsurancePlansRef.current = promise;
        }
      },
      200
    ),
    [clientId, dispatch]
  );

  console.log(formik.errors);

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    pendingGetInsurancePlansRef.current?.abort();
    if (value === '') {
      dispatch(clearInsurancePlans());
    } else {
      debouncedSearch(value, pendingGetInsurancePlansRef);
    }
  };

  return (
    <Stack>
      <LabelComponent text="Insurance Plan" />
      <InsurancePlanSelect
        showTitle={searchInputValue !== '' ? false : true}
        data={insurancePlans.map((insurancePlan) => ({
          id: insurancePlan.id,
          name: insurancePlan.name
        }))}
        value={
          formik.values[insuranceType]?.insurancePlanId
            ? {
                id: formik.values[insuranceType]?.insurancePlanId?.toString(),
                name: formik.values[insuranceType]?.insurancePlanName
              }
            : null
        }
        onClose={() => {
          setSearchInputValue('');
        }}
        multiple={false}
        name="zCodes"
        onBlur={formik.handleBlur}
        searchInputValue={searchInputValue}
        onSearchInputChange={(e) => onSearchInputChange(e)}
        onChange={(e) => {
          const newValue = insurancePlans.find(
            (insurancePlan) => insurancePlan.id.toString() === e.target.value
          );
          if (newValue?.id !== -1) {
            formik.setFieldValue(`${insuranceType}.otherInsurancePlan`, '');
          }
          formik.setFieldValue(`${insuranceType}.insurancePlanId`, newValue?.id);
          formik.setFieldValue(`${insuranceType}.insurancePlanName`, newValue?.name);
          formik.setFieldTouched(`${insuranceType}.insurancePlanId`, true);
          formik.setFieldTouched(`${insuranceType}.insurancePlanName`);
        }}
        label="Select"
      />
    </Stack>
  );
};

export const relationshipToClientOptions = [
  { id: 'AUNT_UNCLE', name: 'Aunt/Uncle' },
  { id: 'CHILD', name: 'Child' },
  { id: 'COUSIN', name: 'Cousin' },
  { id: 'EX_SPOUSE', name: 'Ex-Spouse' },
  { id: 'FATHER', name: 'Father' },
  { id: 'FRIEND', name: 'Friend' },
  { id: 'GRANDPARENT', name: 'Grandparent' },
  { id: 'GUARDIAN', name: 'Guardian' },
  { id: 'MOTHER', name: 'Mother' },
  { id: 'NIECE_NEPHEW', name: 'Niece/Nephew' },
  { id: 'PARENT', name: 'Parent' },
  { id: 'SIBLING', name: 'Sibling' },
  { id: 'SIGNIFICANT_OTHER', name: 'Significant Other' },
  { id: 'SPOUSE', name: 'Spouse' },
  { id: 'STEP_CHILD', name: 'Step-child' },
  { id: 'STEP_PARENT', name: 'Step-parent' },
  { id: 'OTHER', name: 'Other' }
];

const InsuredNotSameAsClientForm = ({
  insuranceType
}: {
  insuranceType: 'primaryInsurance' | 'secondaryInsurance';
}) => {
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();

  const touched = formik.touched[insuranceType];
  const errors = formik.errors[insuranceType];
  if (typeof touched === 'boolean') {
    return null;
  }

  if (typeof errors === 'string') {
    return null;
  }

  return (
    <Stack width="100%">
      <Wrapper alignItems={isTablet ? undefined : 'flex-end'}>
        <Stack flex={3}>
          <LabelComponent text="Insured's Relationship to Client" />
          <Dropdown
            name={`${insuranceType}.relationshipToClient`}
            value={formik.values[insuranceType]?.relationshipToClient ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Select"
            data={relationshipToClientOptions}
          />
          <ErrorContainer
            visible={touched?.relationshipToClient && errors?.relationshipToClient}
            errors={formik.errors}
            name={`${insuranceType}.relationshipToClient`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's Name" />
          <InputField
            name={`${insuranceType}.insuredName`}
            value={formik.values[insuranceType]?.insuredName ?? ''}
            onChange={formik.handleChange}
            placeholder="Enter"
            onBlur={formik.handleBlur}
          />
          <ErrorContainer
            visible={touched?.insuredName && errors?.insuredName}
            errors={formik.errors}
            name={`${insuranceType}.insuredName`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's DOB" />
          <DatePicker
            value={formik.values[insuranceType]?.insuredDateOfBirth ?? ''}
            onChange={(value: string) => {
              formik.setFieldValue(`${insuranceType}.insuredDateOfBirth`, value).then(() => {
                formik.setFieldTouched(`${insuranceType}.insuredDateOfBirth`, true);
              });
            }}
            onBlur={() => formik.setFieldTouched(`${insuranceType}.insuredDateOfBirth`, true)}
            disableFuture
          />
          <ErrorContainer
            visible={touched?.insuredDateOfBirth && errors?.insuredDateOfBirth}
            errors={formik.errors}
            name={`${insuranceType}.insuredDateOfBirth`}
          />
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={2}>
          <LabelComponent text="Insured's Street Address or Location" />
          <InputField
            name={`${insuranceType}.insuredStreetAddress`}
            value={formik.values[insuranceType]?.insuredStreetAddress ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredStreetAddress && errors?.insuredStreetAddress}
            errors={formik.errors}
            name={`${insuranceType}.insuredStreetAddress`}
          />
        </Stack>
        <Stack flex={2}>
          <LabelComponent text="Insured's Street Address Line 2" />
          <InputField
            name={`${insuranceType}.insuredStreetAddress2`}
            value={formik.values[insuranceType]?.insuredStreetAddress2 ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredStreetAddress2 && errors?.insuredStreetAddress2}
            errors={formik.errors}
            name={`${insuranceType}.insuredStreetAddress2`}
          />
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={3}>
          <LabelComponent text="Insured's City" />
          <InputField
            name={`${insuranceType}.insuredCity`}
            value={formik.values[insuranceType]?.insuredCity ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredCity && errors?.insuredCity}
            errors={formik.errors}
            name={`${insuranceType}.insuredCity`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's State" />
          <InputField
            name={`${insuranceType}.insuredState`}
            value={formik.values[insuranceType]?.insuredState ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredState && errors?.insuredState}
            errors={formik.errors}
            name={`${insuranceType}.insuredState`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's Zip Code" />
          <InputField
            name={`${insuranceType}.insuredZipCode`}
            value={formik.values[insuranceType]?.insuredZipCode ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredZipCode && errors?.insuredZipCode}
            errors={formik.errors}
            name={`${insuranceType}.insuredZipCode`}
          />
        </Stack>
      </Wrapper>
    </Stack>
  );
};

const InsuranceForm = ({
  insuranceType
}: {
  insuranceType: 'primaryInsurance' | 'secondaryInsurance';
}) => {
  const insuranceCategories = useAppSelector((state) => state.clients.insurancesAutocomplete.data);
  const formik = useFormikContext<FormValues>();

  const touched = formik.touched[insuranceType];
  const errors = formik.errors[insuranceType];

  if (typeof touched === 'boolean') {
    return null;
  }

  if (typeof errors === 'string') {
    return null;
  }

  console.log(formik.values[insuranceType]?.insurancePlanId);

  return (
    <Stack>
      <Stack mb="10px">
        <Stack mb="16px">
          <SectionTitle
            action={
              insuranceType === 'secondaryInsurance' && (
                <button onClick={() => formik.setFieldValue('secondaryInsurance', null)}>
                  <span className={clsx('material-icons', style.deleteIcon)}>delete</span>
                </button>
              )
            }
          >
            {insuranceType === 'primaryInsurance' ? 'Primary Insurance' : 'Secondary Insurance'}
          </SectionTitle>
        </Stack>
        <StandardCheckbox
          label="Insured is the same as client"
          value={formik.values[insuranceType]?.insuredSameAsClient}
          onChange={(e) =>
            formik.setFieldValue(`${insuranceType}.insuredSameAsClient`, e.target.checked)
          }
          name={`${insuranceType}.insuredSameAsClient`}
          onBlur={formik.handleBlur}
        />
        <ErrorContainer
          visible={touched?.insuredSameAsClient && errors?.insuredSameAsClient}
          errors={formik.errors}
          name={`${insuranceType}.insuredSameAsClient`}
        />
      </Stack>
      {!formik.values[insuranceType]?.insuredSameAsClient && (
        <>
          <InsuredNotSameAsClientForm insuranceType={insuranceType} />
          <Stack className={style.line} mb="16px" />
        </>
      )}
      <Wrapper>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Insurance Category" />
            <Dropdown
              name={`${insuranceType}.insuranceCategory`}
              value={formik.values[insuranceType]?.insuranceCategory ?? ''}
              onChange={(e) => {
                formik.setFieldValue(`${insuranceType}.insuranceCategory`, e.target.value);
                if (e.target.value !== 'OTHER') {
                  formik.setFieldValue(`${insuranceType}.otherInsuranceCategory`, '');
                }
              }}
              onBlur={formik.handleBlur}
              label="Select"
              data={insuranceCategories ?? []}
            />
            <ErrorContainer
              visible={touched?.insuranceCategory && errors?.insuranceCategory}
              errors={formik.errors}
              name={`${insuranceType}.insuranceCategory`}
            />
          </Stack>
          {formik.values[insuranceType]?.insuranceCategory === 'OTHER' && (
            <Stack>
              <LabelComponent text="Other Insurance Category" />
              <InputField
                name={`${insuranceType}.otherInsuranceCategory`}
                value={formik.values[insuranceType]?.otherInsuranceCategory ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <ErrorContainer
                visible={touched?.otherInsuranceCategory && errors?.otherInsuranceCategory}
                errors={formik.errors}
                name={`${insuranceType}.otherInsuranceCategory`}
              />
            </Stack>
          )}
        </Stack>
        <Stack flex={1}>
          <Stack>
            <InsurancePlanField insuranceType={insuranceType} />
          </Stack>
          {formik.values[insuranceType]?.insurancePlanName === 'Other' && (
            <Stack mt="16px" mb="16px">
              <LabelComponent text="Other Insurance Plan" />
              <InputField
                name={`${insuranceType}.otherInsurancePlan`}
                value={formik.values[insuranceType]?.otherInsurancePlan ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <ErrorContainer
                visible={touched?.otherInsurancePlan && errors?.otherInsurancePlan}
                errors={formik.errors}
                name={`${insuranceType}.otherInsurancePlan`}
              />
            </Stack>
          )}
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Client's Member ID Number" />
            <InputField
              name={`${insuranceType}.insuranceNumber`}
              value={formik.values[insuranceType]?.insuranceNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter"
            />
            <ErrorContainer
              visible={touched?.insuranceNumber && errors?.insuranceNumber}
              errors={formik.errors}
              name={`${insuranceType}.insuranceNumber`}
            />
          </Stack>
        </Stack>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Group Number" />
            <InputField
              name={`${insuranceType}.groupNumber`}
              value={formik.values[insuranceType]?.groupNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter"
            />
            <ErrorContainer
              visible={touched?.groupNumber && errors?.groupNumber}
              errors={formik.errors}
              name={`${insuranceType}.groupNumber`}
            />
          </Stack>
        </Stack>
      </Wrapper>
      {insuranceType === 'primaryInsurance' && !formik.values.secondaryInsurance && (
        <Stack width="160px" mt="10px">
          <Button
            icon={<span className="material-icons">add</span>}
            theme="transparent"
            type="button"
            text="Add another"
            handleOnClick={() =>
              formik.setFieldValue('secondaryInsurance', secondaryInsuranceInitialValues)
            }
            size="small"
          />
        </Stack>
      )}
      <Stack
        className={style.line}
        mb={insuranceType === 'secondaryInsurance' ? '24px' : '32px'}
        mt={
          insuranceType === 'primaryInsurance' && !formik.values.secondaryInsurance
            ? '24px'
            : '12px'
        }
      />
    </Stack>
  );
};

const Insurance = () => {
  const formik = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getInsurancesAutocomplete());
  }, []);

  return (
    <AccordionComponent
      accordionTabId="insurance"
      title="Insurance"
      showWarning={isSectionInvalid(formik?.errors, [
        'primaryInsurance.insuranceCategory',
        'primaryInsurance.otherInsuranceCategory',
        'primaryInsurance.insurancePlan',
        'primaryInsurance.otherInsurancePlan',
        'primaryInsurance.insuranceNumber',
        'primaryInsurance.groupNumber',
        'primaryInsurance.insuredName',
        'primaryInsurance.insuredDateOfBirth',
        'primaryInsurance.insuredStreetAddress',
        'primaryInsurance.insuredStreetAddress2',
        'primaryInsurance.insuredCity',
        'primaryInsurance.insuredState',
        'primaryInsurance.insuredZipCode',
        'secondaryInsurance.insuranceCategory',
        'secondaryInsurance.otherInsuranceCategory',
        'secondaryInsurance.insurancePlan',
        'secondaryInsurance.otherInsurancePlan',
        'secondaryInsurance.insuranceNumber',
        'secondaryInsurance.groupNumber',
        'secondaryInsurance.insuredName',
        'secondaryInsurance.insuredDateOfBirth',
        'secondaryInsurance.insuredStreetAddress',
        'secondaryInsurance.insuredStreetAddress2',
        'secondaryInsurance.insuredCity',
        'secondaryInsurance.insuredState',
        'secondaryInsurance.insuredZipCode'
      ])}
    >
      <Stack mt="24px">
        <InsuranceForm insuranceType="primaryInsurance" />
        {formik.values.secondaryInsurance && <InsuranceForm insuranceType="secondaryInsurance" />}
        <Stack display="flex" flexDirection="row" gap="24px" width="100%">
          <Stack flex={1}>
            <LabelComponent text="Insurance Notes" />
            <InputField
              name="insuranceNotes"
              value={formik.values?.insuranceNotes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter"
            />
            <ErrorContainer
              visible={formik.touched?.insuranceNotes && formik.errors?.insuranceNotes}
              errors={formik.errors}
              name="insuranceNotes"
            />
          </Stack>
        </Stack>
      </Stack>
    </AccordionComponent>
  );
};

export default Insurance;
